import React, { useMemo } from "react";
import { Button, Tag, Typography, Descriptions } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { useTheme } from "../../contexts/ThemeContext";
import { useRequestValidation } from "../../shared/hooks/useRequestValidation";
import ReceiptCard from "../ReceiptCard";
import { ValidationContent } from "../ValidationContent";
import "./styles.css";

export const RequestDetailsPage: React.FC = () => {
  const { requestId } = useParams<{ requestId: string }>();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDark = theme === "dark";

  const tc = (base: string) => `${base} ${base}--${isDark ? "dark" : "light"}`;

  const { fullRequestData, isLoadingDetails } = useRequestValidation(requestId);

  const record = useMemo(() => {
    if (!fullRequestData) return null;
    return {
      id: requestId,
      type: (fullRequestData.type || "LOAD") as "LOAD" | "WITHDRAW",
      status: fullRequestData.status,
      requestNumber: fullRequestData.requestNumber,
      user: fullRequestData.user,
      receipt: fullRequestData.receipt,
      linkedTransfer: fullRequestData.linkedTransfer,
      operation: fullRequestData.operation,
      requestData: fullRequestData.requestData,
      rejectionReason: fullRequestData.rejectionReason,
    };
  }, [fullRequestData, requestId]);

  const details = useMemo(() => {
    if (!fullRequestData) return null;
    return {
      ...fullRequestData,
      status: fullRequestData.status || record?.status,
      requestData: {
        retryHistory: fullRequestData.requestData?.retryHistory || [],
        processingSteps: (fullRequestData.requestData?.processingSteps || []).map((step: any) => ({
          ...step,
          timestamp: typeof step.timestamp === "string" ? step.timestamp : new Date(step.timestamp).toISOString(),
          metadata: {
            ...(step.metadata || {}),
            detectedPlatform: step.metadata?.detectedPlatform || "unknown",
            extractedFields: step.metadata?.extractedFields || {},
            comparedTransfers: step.metadata?.comparedTransfers || [],
          },
        })),
      },
    };
  }, [fullRequestData, record?.status]);

  const enrichedRecord = useMemo(() => {
    if (!record) return null;
    return {
      ...record,
      status: fullRequestData?.status || record.status,
      linkedTransfer: fullRequestData?.linkedTransfer || record.linkedTransfer,
    };
  }, [record, fullRequestData]);

  const handleBack = () => navigate("/user-panel/requests");

  // Loading state
  if (isLoadingDetails) {
    return (
      <div className="vp-loading">
        <div style={{ textAlign: "center" }}>
          <div className="vp-spinner" />
          <Typography.Text type="secondary">Cargando solicitud...</Typography.Text>
        </div>
      </div>
    );
  }

  if (!fullRequestData || !record) {
    return (
      <div className="vp-loading">
        <div style={{ textAlign: "center" }}>
          <Typography.Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
            No se encontraron datos de la solicitud.
          </Typography.Text>
          <Button type="link" onClick={handleBack} style={{ color: "#ac4949" }}>Volver a solicitudes</Button>
        </div>
      </div>
    );
  }

  const linkedTransfer = fullRequestData.linkedTransfer;
  const operation = fullRequestData.operation;
  const gamification = fullRequestData.gamification;

  return (
    <div className="vp-page">
      {/* ─── HEADER ─── */}
      <div className={tc("vp-header")}>
        <div className="vp-header-left">
          <Button size="small" icon={<ArrowLeftOutlined />} onClick={handleBack} />
          <Typography.Text strong style={{ fontSize: 13 }}>
            {record.user?.username || record.user?.accountName || "Usuario"}
          </Typography.Text>
          {record.requestNumber && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              #{record.requestNumber}
            </Typography.Text>
          )}
          <Tag
            className="vp-tag"
            color={
              record.status === "COMPLETED" ? "success"
              : record.status === "REJECTED" ? "error"
              : record.status === "CANCELED" ? "default"
              : "blue"
            }
          >
            {record.status}
          </Tag>
          <Tag
            className="vp-tag"
            color={record.type === "WITHDRAW" ? "purple" : "blue"}
          >
            {record.type === "WITHDRAW" ? "RETIRO" : "CARGA"}
          </Tag>
        </div>
      </div>

      {/* ─── MAIN: 3 columns ─── */}
      <div className="vp-main">
        {/* ─── LEFT: Receipt ─── */}
        <div className={tc("vp-col")} style={{ width: "30%", minWidth: 280 }}>
          <div className="vp-scroll" style={{ padding: 12 }}>
            <ReceiptCard loading={false} record={{ ...enrichedRecord!, type: record.type }} details={details} />
          </div>
        </div>

        {/* ─── CENTER: Validation Analysis ─── */}
        <div className={`compact-validation-panel ${tc("vp-col")}`} style={{ width: "28%", minWidth: 260 }}>
          <div className="vp-scroll" style={{ padding: "6px 8px" }}>
            <ValidationContent details={details} record={enrichedRecord} loading={false} onLoad={() => {}} />
          </div>
        </div>

        {/* ─── RIGHT: Result Info ─── */}
        <div className={tc("vp-col")} style={{ flex: 1, minWidth: 0 }}>
          <div className="vp-scroll" style={{ padding: 16 }}>
            {/* Amount processed */}
            <div className={tc("vp-card")} style={{ marginBottom: 12 }}>
              <Typography.Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                Monto Procesado
              </Typography.Text>
              <div style={{ marginTop: 4 }}>
                <span className="vp-amount" style={{ color: "#52c41a" }}>
                  ${operation?.amount?.toFixed(2) || fullRequestData.amount?.toFixed(2) || "0.00"}
                </span>
                {(operation?.bonus > 0 || fullRequestData.bonus > 0) && (
                  <Tag color="green" style={{ marginLeft: 8, fontSize: 11 }}>
                    +${(operation?.bonus || fullRequestData.bonus || 0).toFixed(2)} bono
                  </Tag>
                )}
              </div>
            </div>

            {/* Linked transfer */}
            {linkedTransfer && (
              <div className={tc("vp-card")} style={{ marginBottom: 12 }}>
                <Typography.Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em", display: "block", marginBottom: 8 }}>
                  Transferencia Vinculada
                </Typography.Text>
                <Descriptions size="small" column={1} colon={false}>
                  {linkedTransfer.operationNumber && (
                    <Descriptions.Item label="Operación">{linkedTransfer.operationNumber}</Descriptions.Item>
                  )}
                  {linkedTransfer.coelsaId && (
                    <Descriptions.Item label="CoelsaId">{linkedTransfer.coelsaId}</Descriptions.Item>
                  )}
                  {linkedTransfer.amount != null && (
                    <Descriptions.Item label="Monto">${linkedTransfer.amount?.toFixed(2)}</Descriptions.Item>
                  )}
                  {linkedTransfer.sourcePlatform && (
                    <Descriptions.Item label="Plataforma">{linkedTransfer.sourcePlatform}</Descriptions.Item>
                  )}
                  {linkedTransfer.dateTime && (
                    <Descriptions.Item label="Fecha">
                      {dayjs(linkedTransfer.dateTime).format("DD/MM/YYYY HH:mm")}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </div>
            )}

            {/* Rejection reason */}
            {record.status === "REJECTED" && fullRequestData.rejectionReason && (
              <div className={tc("vp-card")} style={{ marginBottom: 12 }}>
                <Typography.Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em", display: "block", marginBottom: 8 }}>
                  Motivo de Rechazo
                </Typography.Text>
                <Typography.Text type="danger">{fullRequestData.rejectionReason}</Typography.Text>
              </div>
            )}

            {/* Dates */}
            <div className={tc("vp-card")} style={{ marginBottom: 12 }}>
              <Typography.Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em", display: "block", marginBottom: 8 }}>
                Fechas
              </Typography.Text>
              <Descriptions size="small" column={1} colon={false}>
                {fullRequestData.createdAt && (
                  <Descriptions.Item label="Creación">
                    {dayjs(fullRequestData.createdAt).format("DD/MM/YYYY HH:mm:ss")}
                  </Descriptions.Item>
                )}
                {fullRequestData.updatedAt && (
                  <Descriptions.Item label="Procesamiento">
                    {dayjs(fullRequestData.updatedAt).format("DD/MM/YYYY HH:mm:ss")}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </div>

            {/* Gamification result */}
            {gamification && (
              <div className={tc("vp-card")} style={{ marginBottom: 12 }}>
                <Typography.Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em", display: "block", marginBottom: 8 }}>
                  Gamificación
                </Typography.Text>
                <Descriptions size="small" column={1} colon={false}>
                  {gamification.totalBonus != null && (
                    <Descriptions.Item label="Bono Total">
                      <Tag color="green">${gamification.totalBonus?.toFixed(2)}</Tag>
                    </Descriptions.Item>
                  )}
                  {gamification.userLevel && (
                    <Descriptions.Item label="Nivel">
                      <Tag color="blue">
                        Nv. {gamification.userLevel.level} - {gamification.userLevel.name}
                        {gamification.userLevel.multiplier > 1 && ` (x${gamification.userLevel.multiplier})`}
                      </Tag>
                    </Descriptions.Item>
                  )}
                  {gamification.rulesApplied?.length > 0 && (
                    <Descriptions.Item label="Reglas">
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                        {gamification.rulesApplied.map((rule: any, i: number) => (
                          <Tag key={i} color="orange" style={{ fontSize: 10 }}>
                            {rule.ruleName}: +${rule.bonusAmount?.toFixed(2)}
                          </Tag>
                        ))}
                      </div>
                    </Descriptions.Item>
                  )}
                  {gamification.points != null && (
                    <Descriptions.Item label="Puntos">
                      <Tag color="gold">{gamification.points} pts</Tag>
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequestDetailsPage;
